import moment from 'moment';
import {
  CampaignWeekdayOrdinal,
  CampaignAcceptableTime,
} from '@mm/state/model/CampaignsModel';

export const EVERY_DAY = [0, 1, 2, 3, 4, 5, 6] as CampaignWeekdayOrdinal[];

const formatDayOfWeek = (dayOfWeek: CampaignWeekdayOrdinal) =>
  moment().day(dayOfWeek).format('ddd');

export const formatDaysofWeek = (
  daysOfWeek: Set<CampaignWeekdayOrdinal>,
): string => {
  if (daysOfWeek.size === 0 || daysOfWeek.size === 7) {
    return 'Every day';
  }
  let formattedParts = '';

  // The window keeps track of weekdays we haven't outputted.
  let windowStart = 0;
  let windowEnd = 6;
  if (
    daysOfWeek.has(windowEnd as CampaignWeekdayOrdinal) &&
    daysOfWeek.has(windowStart as CampaignWeekdayOrdinal)
  ) {
    // Special case for range wrapping around the end of the week.
    while (daysOfWeek.has((windowEnd - 1) as CampaignWeekdayOrdinal)) {
      if (windowEnd === 1) {
        break;
      }
      windowEnd -= 1;
    }
    while (daysOfWeek.has((windowStart + 1) as CampaignWeekdayOrdinal)) {
      if (windowStart === 6) {
        break;
      }
      windowStart += 1;
    }
    if (windowEnd !== 6 || windowStart !== 0) {
      formattedParts += `${formatDayOfWeek(
        windowEnd as CampaignWeekdayOrdinal,
      )}-${formatDayOfWeek(windowStart as CampaignWeekdayOrdinal)}, `;
      windowStart += 1;
      windowEnd -= 1;
    }
  }
  let firstDaySeenInARow = undefined as CampaignWeekdayOrdinal | undefined;
  for (let i = windowStart as CampaignWeekdayOrdinal; i <= windowEnd; i += 1) {
    if (daysOfWeek.has(i)) {
      if (firstDaySeenInARow === undefined) {
        firstDaySeenInARow = i;
      }
      if (!daysOfWeek.has((i + 1) as CampaignWeekdayOrdinal)) {
        if (firstDaySeenInARow === i) {
          formattedParts += `${formatDayOfWeek(i)}, `;
        } else if (firstDaySeenInARow + 1 === i) {
          formattedParts += `${formatDayOfWeek(
            firstDaySeenInARow,
          )}, ${formatDayOfWeek(i)}, `;
        } else {
          formattedParts += `${formatDayOfWeek(
            firstDaySeenInARow,
          )}-${formatDayOfWeek(i)}, `;
        }
        firstDaySeenInARow = undefined;
      }
    }
  }
  return formattedParts.slice(0, -2);
};

export const formatTime = (time: string) =>
  moment(time, 'HH:mm').format('h:mm A');

export const START_OF_WHOLE_DAY = '00:00';
export const END_OF_WHOLE_DAY = '00:00';

export const formatAcceptableTime = (
  acceptableTime: CampaignAcceptableTime,
  allowNextDayEndTimes: boolean = true,
) => {
  const { daysOfWeek, startTime, stopTime } = acceptableTime;

  const effectiveDaysOfWeek = daysOfWeek || EVERY_DAY;
  const effectiveStartTime = startTime || START_OF_WHOLE_DAY;
  const effectiveStopTime = stopTime || END_OF_WHOLE_DAY;

  const daysOfWeekSet = new Set(effectiveDaysOfWeek);

  const wholeDay =
    effectiveStartTime === START_OF_WHOLE_DAY &&
    effectiveStopTime === END_OF_WHOLE_DAY;
  const displayTime = wholeDay
    ? 'All day'
    : `${formatTime(effectiveStartTime)}-${formatTime(effectiveStopTime)}${
        effectiveStartTime > effectiveStopTime
          ? ` (${allowNextDayEndTimes ? '+1' : 'invalid'})`
          : ''
      }`;

  return `${formatDaysofWeek(daysOfWeekSet)}, ${displayTime}`;
};

export const formatMonthDayTime = (time: number | undefined) => {
  if (time === undefined || Number.isNaN(time)) {
    return '';
  }
  const mTime = moment(time);
  const monthAndDay = mTime.format('MMM D');
  const hoursMinutes = mTime.format('h:mm a').toUpperCase();
  return `${monthAndDay}th at ${hoursMinutes}`;
};
